import { useState, useCallback } from 'react';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { configToWagtail } from '../../utils/configToWagtail';
import s from './SaveToWagtail.module.scss';

const SaveToWagtail = ({ config, wagtailSource, onClearSource }) => {
    const [saving, setSaving] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [confirmPublish, setConfirmPublish] = useState(false);

    const save = useCallback(
        async (publish) => {
            setFeedback(null);
            setSaving(true);

            const result = configToWagtail(config);
            if (!result.success) {
                setFeedback({
                    type: 'error',
                    message: result.error || 'Failed to convert config',
                });
                setSaving(false);
                return;
            }

            try {
                const response = await fetchWithAuth('/wt/fenix/graphs/save/', {
                    method: 'POST',
                    body: JSON.stringify({
                        location: {
                            page_id: wagtailSource.page_id,
                            field_name: wagtailSource.field_name,
                            section_index: wagtailSource.section_index,
                            content_index: wagtailSource.content_index,
                        },
                        block_json: result.json,
                        publish,
                    }),
                });

                if (!response) return;

                if (response.status === 403) {
                    setFeedback({
                        type: 'error',
                        message: "You don't have permission to edit this page",
                    });
                } else if (response.status === 409) {
                    const data = await response.json();
                    setFeedback({
                        type: 'error',
                        message:
                            data.detail ||
                            'Block location is stale — re-import from Wagtail',
                    });
                } else if (!response.ok) {
                    const data = await response.json().catch(() => ({}));
                    setFeedback({
                        type: 'error',
                        message:
                            data.detail || `Save failed (${response.status})`,
                    });
                } else {
                    setFeedback({
                        type: 'success',
                        message: publish
                            ? 'Published successfully'
                            : 'Draft saved',
                    });
                }
            } catch (err) {
                setFeedback({
                    type: 'error',
                    message: err.message || 'Network error',
                });
            } finally {
                setSaving(false);
                setConfirmPublish(false);
            }
        },
        [config, wagtailSource]
    );

    const handlePublishClick = useCallback(() => {
        setConfirmPublish(true);
        setFeedback(null);
    }, []);

    const breadcrumb = [
        wagtailSource.dashboardTitle,
        wagtailSource.pageTitle,
        wagtailSource.blockTitle,
    ]
        .filter(Boolean)
        .join(' > ');

    return (
        <div className={s.Root}>
            <div className={s.SourceInfo}>
                <span className={s.SourceLabel}>Editing:</span>
                <span className={s.SourcePath}>{breadcrumb}</span>
                <button
                    className={s.UnlinkButton}
                    onClick={onClearSource}
                    title="Unlink from Wagtail source">
                    &times;
                </button>
            </div>

            <div className={s.Actions}>
                <button
                    className={s.DraftButton}
                    onClick={() => save(false)}
                    disabled={saving}>
                    {saving ? 'Saving...' : 'Save Draft'}
                </button>
                <button
                    className={s.PublishButton}
                    onClick={handlePublishClick}
                    disabled={saving}>
                    Publish
                </button>
            </div>

            {confirmPublish && (
                <div className={s.Confirm}>
                    <span className={s.ConfirmText}>
                        This will update the live page. Continue?
                    </span>
                    <div className={s.ConfirmActions}>
                        <button
                            className={s.ConfirmCancel}
                            onClick={() => setConfirmPublish(false)}>
                            Cancel
                        </button>
                        <button
                            className={s.ConfirmYes}
                            onClick={() => save(true)}
                            disabled={saving}>
                            {saving ? 'Publishing...' : 'Yes, Publish'}
                        </button>
                    </div>
                </div>
            )}

            {feedback && (
                <div
                    className={`${s.Feedback} ${feedback.type === 'error' ? s.Error : s.Success}`}>
                    {feedback.message}
                </div>
            )}
        </div>
    );
};

export default SaveToWagtail;
